import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { IPlano, IPlanoCliente } from "../../interfaces/IPlanoCliente";
import PlanoClienteService from "../../services/PlanoClienteService";
import planoNovaService from "../../services/planoNovaService";

interface Props {
  clienteId: number;
  ativo: boolean;
}

function ClientesPlanoTab({ clienteId, ativo }: Props) {
  const [vinculos, setVinculos] = useState<IPlanoCliente[]>([]);
  const [todosPlanos, setTodosPlanos] = useState<IPlano[]>([]);
  const [novoPlanoId, setNovoPlanoId] = useState<number>(0);
  const [novoQtd, setNovoQtd] = useState<number>(1);
  const [adicionando, setAdicionando] = useState<boolean>(false);
  const [qtdsEditadas, setQtdsEditadas] = useState<Record<number, number>>({});
  const [salvando, setSalvando] = useState<Record<number, boolean>>({});
  const [carregado, setCarregado] = useState<boolean>(false);

  useEffect(() => {
    if (!ativo) return;
    carregarDados();
    // eslint-disable-next-line
  }, [ativo, clienteId]);

  async function carregarDados() {
    setCarregado(false);
    const [vinculosData, planosData] = await Promise.all([
      PlanoClienteService.listarPorCliente(clienteId),
      planoNovaService.listarTodos()
    ]);
    setVinculos(vinculosData);
    setTodosPlanos(planosData);
    const qtds: Record<number, number> = {};
    vinculosData.forEach(v => { qtds[v.id] = v.qtd; });
    setQtdsEditadas(qtds);
    setCarregado(true);
  }

  const planosDisponiveis = todosPlanos.filter(
    p => !vinculos.some(v => v.planoId === p.id)
  );

  async function handleAdicionar() {
    if (!novoPlanoId || novoQtd <= 0) return;
    setAdicionando(true);
    const novo = await PlanoClienteService.adicionar(clienteId, novoPlanoId, novoQtd);
    if (novo) {
      setVinculos(prev => [...prev, novo]);
      setQtdsEditadas(prev => ({ ...prev, [novo.id]: novo.qtd }));
      setNovoPlanoId(0);
      setNovoQtd(1);
    }
    setAdicionando(false);
  }

  async function handleSalvarQtd(vinculo: IPlanoCliente) {
    const qtd = qtdsEditadas[vinculo.id];
    if (!qtd || qtd <= 0) return;
    setSalvando(prev => ({ ...prev, [vinculo.id]: true }));
    const atualizado = await PlanoClienteService.atualizarQtd(clienteId, vinculo.id, qtd);
    if (atualizado) {
      setVinculos(prev => prev.map(v => v.id === vinculo.id ? atualizado : v));
    }
    setSalvando(prev => ({ ...prev, [vinculo.id]: false }));
  }

  async function handleRemover(vinculo: IPlanoCliente) {
    const confirm = await Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-modal-excluir ms-5',
        cancelButton: 'btn btn-modal-cancelar'
      },
      buttonsStyling: false
    }).fire({
      title: 'Remover plano',
      text: `Deseja remover o plano "${vinculo.planoNome}" deste cliente?`,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Remover',
      reverseButtons: true
    });

    if (!confirm.isConfirmed) return;
    const ok = await PlanoClienteService.remover(clienteId, vinculo.id);
    if (ok) {
      setVinculos(prev => prev.filter(v => v.id !== vinculo.id));
    }
  }

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px', minHeight: '300px', display: ativo ? 'block' : 'none' }}>

      {/* Formulário de vínculo */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', paddingBottom: '20px' }}>
        <div className="col-md-5">
          <label className="form-label required">Plano</label>
          <select
            className="form-select"
            value={novoPlanoId}
            onChange={e => setNovoPlanoId(Number(e.target.value))}
          >
            <option value={0} hidden>Selecione um plano</option>
            {planosDisponiveis.map(p => (
              <option key={p.id} value={p.id}>{p.nome}</option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label required">Qtd. Importações</label>
          <input
            type="number"
            min={1}
            className="form-control"
            value={novoQtd}
            onChange={e => setNovoQtd(Number(e.target.value))}
          />
        </div>
        <button
          disabled={adicionando || !novoPlanoId || novoQtd <= 0}
          onClick={handleAdicionar}
          style={{ height: '40px', marginBottom: '1px' }}
          className="btn btn-sm btn-primario"
          title="Vincular plano"
        >
          {adicionando
            ? <span className="spinner-border spinner-border-sm" />
            : <i className="icon-lg text-white fas fa-plus" />}
        </button>
      </div>

      {/* Tabela de planos vinculados */}
      {!carregado && <p style={{ color: '#A3A3A3', fontFamily: 'Merriweather Sans' }}>Carregando...</p>}

      {carregado && vinculos.length === 0 && (
        <p style={{ color: '#A3A3A3', fontFamily: 'Merriweather Sans' }}>Nenhum plano vinculado a este cliente.</p>
      )}

      {carregado && vinculos.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0' }}>
              <th style={{ color: '#5E6397', fontFamily: 'Assistant', fontSize: '16px', padding: '10px 8px', textAlign: 'left' }}>Plano</th>
              <th style={{ color: '#5E6397', fontFamily: 'Assistant', fontSize: '16px', padding: '10px 8px', width: '160px' }}>Qtd. Importações</th>
              <th style={{ color: '#5E6397', fontFamily: 'Assistant', fontSize: '16px', padding: '10px 8px', width: '80px', textAlign: 'center' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {vinculos.map(v => (
              <tr key={v.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                <td style={{ color: '#525252', fontFamily: 'Merriweather Sans', fontSize: '1em', padding: '10px 8px' }}>
                  {v.planoNome}
                </td>
                <td style={{ padding: '8px' }}>
                  <input
                    type="number"
                    min={1}
                    className="form-control form-control-sm"
                    value={qtdsEditadas[v.id] ?? v.qtd}
                    onChange={e => setQtdsEditadas(prev => ({ ...prev, [v.id]: Number(e.target.value) }))}
                  />
                </td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  {salvando[v.id]
                    ? <span className="spinner-border spinner-border-sm" style={{ color: '#5E6397' }} />
                    : (
                      <>
                        <i
                          onClick={() => handleSalvarQtd(v)}
                          className="fas fa-save"
                          style={{ color: '#5E6397', cursor: 'pointer', marginRight: '16px' }}
                          title="Salvar quantidade"
                        />
                        <i
                          onClick={() => handleRemover(v)}
                          className="fas fa-trash-alt"
                          style={{ color: '#5E6397', cursor: 'pointer' }}
                          title="Remover plano"
                        />
                      </>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

    </div>
  );
}

export default ClientesPlanoTab;
